import {retrieveFromCache} from "../manageStoreCacheData";
import {REPLICA_LIST, REPLICALIST_STOREDLIST, GLOBALS, AUTHENTICATION_KEY} from "../../modules/_MODULE_GLOBALS/constants";
import {getStoreValue} from "../storeValue";
import {isEmpty, isTrue} from "../generalUtils";
import {getCookie, COOKIE_NAMES} from "../standardizedCookies";


/**
 * Called by one of the following modules that use an replica list as their source for replica
 * data:
 *     replicaList
 *     replicaViewer
 *
 * This function will attempt to get a stored replica list from hash storage.  If it finds
 * a list with a key that matches the query parameters, it will just call updateData to
 * return the list without fetch.  If not, it will call fetch to get a new list.
 *
 * Note: This will use the stored, generic updateData and fetchData and just pass the
 * appropriate parameters.  This will trigger the appropriate reducer in the replica.js
 * reducer file, which the appropriate module (identified by storageKey) will use to
 * determine if the list is for its use.
 *
 * @param params module parameters
 *     mops: mops from module
 */
const getReplicaList = (params) => {
	params = Object.assign({
		storageKey: '',
		queryParams: {},
		returnStoredDataOnly: false
	}, params);
	const storageKey = !isEmpty(params.storageKey) ? params.storageKey : '';
	// Shallow-copy so the subscriberId/userAccessHash mutations below don't leak back into the
	// caller's object (mops.queryParams), which is also used as a change-detection dependency.
	const queryParams = !isEmpty(params.queryParams) ? { ...params.queryParams } : {};

	// subscriberId must be added to queryParams BEFORE the cache lookup so that the hash key
	// used here matches the hash key that was used when the data was stored in the reducer
	// (which stores using fetchQueryParams, which includes subscriberId).
	let subscriberId = getStoreValue({type: GLOBALS, storageKey: "globals", attributeKey:"subscriberId"});
	const loginId = getStoreValue({type: GLOBALS, storageKey: "globals", attributeKey:"loginId"});
	// Fall back to the lookinside_ids cookie when globals.lookinsideIds is empty — a regular (email)
	// login clears globals.lookinsideIds, but the per-issue lookinside grants persist in the cookie.
	// The authentication component does the same fallback; without it here, the replica fetch for a
	// lookinside-only issue (e.g. the logged-in user doesn't own it) would miss the per-issue
	// lookinside id and fall back to the login id, so the server returns no page access (low-res).
	const storeLookinsideIds = getStoreValue({type: GLOBALS, storageKey: "globals", attributeKey:"lookinsideIds"});
	const lookinsideIds = !isEmpty(storeLookinsideIds) ? storeLookinsideIds : getCookie(null, COOKIE_NAMES.LOOKINSIDE_IDS);
	const issueUrl = queryParams.issueUrl
		|| getStoreValue({type: GLOBALS, storageKey: "globals", attributeKey:"currentIssueUrl"});

	// Whether this request is under a subscriber/lookinside context. For NO_LOGIN (and any other
	// type with no subscriber/lookinside identity) all three of these are empty, so the
	// subscriberId-injection and userAccessHash-removal below are skipped and the cache key is
	// built exactly as on master. This keeps NO_LOGIN behavior unchanged by this branch.
	const isSubscriberContext = !isEmpty(subscriberId) || !isEmpty(loginId) || !isEmpty(lookinsideIds);

	// Resolve the subscriberId that belongs to THIS issue, not the globally-current one.
	// globals.subscriberId tracks whichever issue was last activated, so after visiting issue2
	// it holds issue2's lookinside id. Using it for issue1's cache key produces a different hash
	// than the one stored on issue1's first visit, causing a cache miss and a redundant refetch.
	// Whenever a per-issue lookinsideId exists for this issueUrl, always prefer it so the key is
	// stable per issue regardless of navigation order.
	//
	// staleSubscriberId: true when this is a lookinside user (lookinsideIds present) but the
	// per-issue lookinsideId for THIS issueUrl hasn't been resolved yet (login for the issue is
	// still pending). In that window globals.subscriberId still holds the PREVIOUS issue's id, so
	// fetching now would (a) hit the backend with the wrong subscriberId and (b) cache the result
	// under the wrong key — which is then never re-read once the correct id resolves, producing a
	// redundant fetch on every entry. We suppress the fetch in this window; the authentication
	// flow triggers the fetch again once lookinsideIds[issueUrl] is set.
	// Does the issue have a per-issue lookinside id available (store or cookie)?
	const hasLookinsideIdForIssue = !isEmpty(lookinsideIds) && !isEmpty(issueUrl) && !isEmpty(lookinsideIds[issueUrl]);

	// Read the logged-in user's per-issue access + accessPages from the auth slice. The server reports
	// BOTH a truly owned issue AND a lookinside preview as access=true; only the accessPages list
	// separates them (owned = empty, preview = [1..5]). So "owned" requires access===true AND an
	// empty accessPages. A non-empty accessPages means a page-limited preview, which must use the
	// per-issue lookinside id — using the login id there grants an unowned issue full-res pages.
	const authAccess = getStoreValue({storageKey: AUTHENTICATION_KEY, attributeKey: "userAccess.access"});
	const authAccessPages = getStoreValue({storageKey: AUTHENTICATION_KEY, attributeKey: "userAccess.accessPages"});
	// Owned = access true with no page restriction. A non-empty accessPages for this issue means a
	// lookinside page-limited preview, NOT ownership, even though the server marks it access=true.
	const issueHasAccess = !isEmpty(authAccess) && isTrue(authAccess[issueUrl], {defaultValue: false});
	const issueIsPageRestricted = !isEmpty(authAccessPages) && !isEmpty(authAccessPages[issueUrl]);
	const issueIsOwned = issueHasAccess && !issueIsPageRestricted;
	// Use the per-issue lookinside id for any issue the user does NOT fully own that has one
	// available (store or cookie). This covers the pre-login visitor AND the logged-in user viewing
	// an unowned issue (page-limited preview).
	const useLookinsideIdForIssue = hasLookinsideIdForIssue && !issueIsOwned;

	let staleSubscriberId = false;
	if (useLookinsideIdForIssue) {
		subscriberId = lookinsideIds[issueUrl];
	} else if (!isEmpty(lookinsideIds) && !isEmpty(issueUrl) && isEmpty(loginId) && !hasLookinsideIdForIssue) {
		// Lookinside user, but no per-issue id resolved yet and no email login to fall back on.
		staleSubscriberId = true;
	}

	if (!isEmpty(subscriberId)) {
		queryParams.subscriberId = subscriberId;
	}

	// Remove userAccessHash from the replica query params. It is a hash of the ENTIRE userAccess
	// object (access/accessPages for ALL issues), which mutates as the user visits other issues
	// under lookinside (SUBSCRIBER_LOGIN_LI). Because it is part of the cache key AND is recomputed
	// per render/instance, re-entering a previously-cached issue produced a different key and a
	// redundant backend fetch. The issue's identity is already captured by issueUrl + subscriberId
	// + isLoggedIn, which are stable per issue; a genuine access change for the issue is reflected
	// by subscriberId/isLoggedIn. Dropping it here keeps both the cache lookup and the fetch keyed
	// on stable, per-issue values. Scoped to the replica path only (this function).
	//
	// Only strip it in a subscriber/lookinside context. For NO_LOGIN (no subscriber identity) the
	// key must stay identical to master — otherwise stripping it changes the cache key and causes
	// extra WSCollectionLibrary/WSCollectionLibraryContent fetches for collections this branch
	// isn't meant to affect.
	if (isSubscriberContext && queryParams.hasOwnProperty('userAccessHash')) {
		delete queryParams.userAccessHash;
	}

	const storedListData = retrieveFromCache({dataKey: queryParams, keyType: 'replicaPages', emptyDataObject: []});
	const storedListStyleToc = retrieveFromCache({dataKey: queryParams, keyType: 'listStyleToc', emptyDataObject: ''});

	if (params.returnStoredDataOnly) {
		return storedListData.storedData;
	} else {
		if (storedListData.hasStoredData) {
			//console.log('getReplicaList.js: No FETCH');
			const updateData = getStoreValue({attributeKey: 'genericUpdateData'});
			updateData({replicaList: storedListData.storedData, listStyleToc: storedListStyleToc.storedData},
				{ storageKey: storageKey, fetchQueryParams: queryParams, type: REPLICALIST_STOREDLIST });
		} else if (staleSubscriberId) {
			// Don't fetch with a borrowed/stale subscriberId — wait for the per-issue lookinside
			// id to resolve. The authentication flow re-triggers the fetch once it's available.
			//console.log('getReplicaList.js: SKIP FETCH — per-issue subscriberId not resolved yet');
		} else {
			//console.log('getRepicaList.js: FETCHING NEW LIST', queryParams);
			const fetchData = getStoreValue({ attributeKey: 'genericFetchData' });
			fetchData({storageKey: storageKey, fetchQueryParams: queryParams, queryParams: queryParams, type: REPLICA_LIST});
		}
	}
};
export {getReplicaList};
